import { useMemo, useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  Avatar, Box, Button, Chip, CircularProgress,
  IconButton, InputAdornment, MenuItem, Paper,
  Stack, Tooltip, Typography, FormControl, InputLabel,
  Select, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material'
import {
  TrendingUp as DealIcon,
  Add as AddIcon,
  Search as SearchIcon,
  EmojiEvents as WonIcon,
  Cancel as LostIcon,
  Edit as EditIcon,
  AttachMoney as RevenueIcon,
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon,
  KeyboardArrowUp as ScrollTopIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import { Check as CheckIcon } from '@mui/icons-material'
import { EntityModal } from '../components/shared/EntityModal'
import { PageHeader } from '../components/shared/PageHeader'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { ListToolbar, type BulkAction } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters, EMPTY_PRICE_FILTER } from '../components/shared/UnifiedFilterDialog'
import { themeColors } from '../app/layout/theme'
import { DataTable, type Column } from '../components/ui/DataTable'
import { leadsApi } from '../lib/api/leadsApi'
import { DeleteActionIcon, EditActionIcon } from '../components/ui/ActionIcons'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { authStore } from '../lib/stores/authStore'
import { usersAdminApi, type AdminUser } from '../lib/api/usersAdminApi'
import {
  dealsApi, Deal, DealStatus, DealFilters, CreateDealPayload, UpdateDealPayload,
  PipelineStage, DEAL_STATUS_LABELS,
} from '../lib/api/dealsApi'
import { getCustomerTypes, type CustomerType } from '../lib/api/customerTypesApi'
import { DealDrawer, type DealDrawerValues } from '../components/drawers/DealDrawer'
import { salesApi } from '../lib/api/salesApi'
import { FormInput, FormSelect } from '../components/ui'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import { EditDealModal, MarkLostModal } from './DealDetailPage'
import { usePermission } from '../lib/hooks/usePermission'
import { useDealSourceFilterOptions } from '../lib/hooks/useDealSourceFilterOptions'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAmount(amount: number | null, currency: string) {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(amount)
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  // let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  // return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
  return `${themeColors.primary}`
}
function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

const STAGE_COLORS = [
  { match: /qual/i, bg: '#D5E3FF', color: '#003AB8' },
  { match: /demo|making/i, bg: '#FFFBEB', color: '#8E6700' },
  { match: /proposal|quotation/i, bg: '#FFE0F7', color: '#9C008C' },
  { match: /negoti/i, bg: '#DFFEFF', color: '#007276' },
  { match: /ready/i, bg: '#E8FFE3', color: '#197C00' },
  { match: /won/i, bg: '#ECFDF5', color: '#007E54' },
  { match: /lost/i, bg: '#FDECEC', color: '#B90004' },
]

function stageColor(stage: string) {
  return STAGE_COLORS.find(s => s.match.test(stage)) ?? { bg: '#EDE7FF', color: '#5C35D8' }
}

// ── Mark Status Dialog ────────────────────────────────────────────────────────
function MarkStatusDialog({ open, deal, targetStatus, onClose }: {
  open: boolean; deal: Deal | null; targetStatus: DealStatus; onClose: () => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const mutation = useMutation({
    mutationFn: () => dealsApi.changeStatus(deal!.id, targetStatus),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['deals-list'] })
      qc.invalidateQueries({ queryKey: ['deals-kanban'] })
      qc.invalidateQueries({ queryKey: ['deals-stats'] })
      toast.success((targetStatus === 'SALES' || targetStatus === 'WON') ? 'Deal moved to Sales!' : `Deal marked as ${DEAL_STATUS_LABELS[targetStatus]}`)
      onClose()
      if (targetStatus === 'SALES' || targetStatus === 'WON') {
        try {
          const salesRecord = await salesApi.getByDealId(deal!.id)
          if (salesRecord?.id) {
            navigate(`/sales/${salesRecord.id}`)
          }
        } catch (e) {
          // ignore
        }
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update status'),
  })
  const isSalesOrWon = targetStatus === 'SALES' || targetStatus === 'WON'
  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={() => mutation.mutate()}
      title={isSalesOrWon ? 'Move to Sales' : `Mark as ${DEAL_STATUS_LABELS[targetStatus]}`}
      submitLabel={isSalesOrWon ? 'Move to Sales' : `Mark ${DEAL_STATUS_LABELS[targetStatus]}`}
      isSubmitting={mutation.isPending}
      maxWidth="xs"
      icon={isSalesOrWon ? <ArrowForwardIcon sx={{ fontSize: 18 }} /> : <CheckIcon sx={{ fontSize: 18 }} />}
      submitProps={{ color: isSalesOrWon ? 'primary' : 'error' }}
    >
      <Typography variant="body2" color="text.secondary">
        {isSalesOrWon ? (
          <>Move <strong>{deal?.title}</strong> to <strong>Sales</strong>?</>
        ) : (
          <>Mark <strong>{deal?.title}</strong> as{' '}
          <strong>{DEAL_STATUS_LABELS[targetStatus]}</strong>?</>
        )}
        {isSalesOrWon ? ' 🎉 Congratulations!' : ''}
      </Typography>
    </EntityModal>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// ── Kanban Board ──────────────────────────────────────────────────────────────

function DealsKanbanCard({ deal, onOpen, canDrag = true, onDragStart: onDragStartProp, onDragEnd: onDragEndProp }: {
  deal: Deal
  onOpen: () => void
  canDrag?: boolean
  onDragStart?: () => void
  onDragEnd?: () => void
}) {
  const isImmutable = deal.status !== 'OPEN'
  const isDraggable = !isImmutable && canDrag
  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      draggable={isDraggable}
      onDragStart={(e) => {
        if (!isDraggable) return
        e.dataTransfer.setData('text/plain', deal.id)
        onDragStartProp?.()
      }}
      onDragEnd={() => onDragEndProp?.()}
      sx={{
        p: 1.5, mb: 1, borderRadius: '10px',
        border: `1px solid ${themeColors.border}`,
        cursor: isDraggable ? 'grab' : 'pointer', bgcolor: '#fff',
        opacity: isImmutable ? 0.85 : 1,
        '&:hover': { borderColor: isImmutable ? themeColors.border : themeColors.primary, boxShadow: isImmutable ? 'none' : `0 2px 10px ${themeColors.primary}18` },
        '&:active': { cursor: isDraggable ? 'grabbing' : 'pointer' },
        transition: 'all 0.15s',
      }}
    >
      <Typography fontWeight={700} fontSize="13px" color="text.primary" sx={{ lineHeight: 1.3, mb: 0.5 }}>
        {deal.title}
      </Typography>

      <Typography fontSize="12px" color="text.secondary" sx={{ mb: 1 }}>
        {deal.customer?.companyName || deal.customer?.name || deal.lead?.name || deal.contact?.name || '—'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
        <Typography fontSize="12.5px" fontWeight={700} color={themeColors.primary}>
          {formatAmount(deal.amount, deal.currency)}
        </Typography>
        {deal.owner && (
          <Avatar sx={{ width: 22, height: 22, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(deal.owner.name) }}>
            {initials(deal.owner.name)}
          </Avatar>
        )}
      </Box>
    </Paper>
  )
}

function DealKanbanColumn({
  stage,
  stageCount,
  baseFilters,
  canDrag,
  onOpen,
  onAddDeal,
  onDropDeal,
  setIsDraggingBoard,
}: {
  stage: PipelineStage
  stageCount?: { count: number; totalAmount: number }
  baseFilters: DealFilters
  canDrag: boolean
  onOpen: (deal: Deal) => void
  onAddDeal: (stage?: string) => void
  onDropDeal: (dealId: string, targetStageName: string) => void
  setIsDraggingBoard: (val: boolean) => void
}) {
  const [isOver, setIsOver] = useState(false)
  const color = stage.color || '#6366f1'
  const isClosedStage = stage.id === 'virtual-sales' || stage.isWon || stage.isLost

  const colFilters = useMemo(() => {
    const filters: DealFilters = { ...baseFilters, pageSize: 20 }
    if (stage.id === 'virtual-sales' || stage.name === 'Sales') {
      filters.statuses = ['SALES']
    } else if (stage.isWon) {
      filters.statuses = ['WON']
    } else if (stage.isLost) {
      filters.statuses = ['LOST']
    } else {
      filters.stages = [stage.name]
    }
    return filters
  }, [baseFilters, stage])

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['deals-kanban-col', stage.name, colFilters],
    queryFn: ({ pageParam = 1 }) =>
      dealsApi.getAll({ ...colFilters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.flatMap(p => p.items).length
      return fetched < lastPage.total ? allPages.length + 1 : undefined
    },
    staleTime: 0,
  })

  const stageDeals = useMemo(() => {
    if (!data?.pages) return []
    const map = new Map<string, Deal>()
    for (const page of data.pages) {
      for (const item of page.items) {
        map.set(item.id, item)
      }
    }
    return Array.from(map.values())
  }, [data])

  const count = stageCount?.count ?? data?.pages?.[0]?.total ?? stageDeals.length

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setIsOver(true) }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsOver(false)
        }
      }}
      onDrop={(e) => {
        e.preventDefault()
        const dealId = e.dataTransfer.getData('text/plain')
        if (dealId) onDropDeal(dealId, stage.name)
        setIsOver(false)
        setIsDraggingBoard(false)
      }}
      sx={{
        flexShrink: 0, width: 280,
        display: 'flex', flexDirection: 'column',
        borderRadius: '12px',
        bgcolor: isOver ? `${color}18` : 'transparent',
        borderLeft: isOver ? `4px solid ${color}` : '4px solid transparent',
        boxShadow: isOver ? `inset 0 0 0 1.5px ${color}40` : 'none',
        transition: 'background-color 0.15s ease, border-left 0.15s ease, box-shadow 0.15s ease',
        px: isOver ? 0.5 : 0,
      }}
    >
      {/* Sticky column header */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 1.5, px: 0.5, py: 0.75, borderRadius: '8px',
        bgcolor: isOver ? `${color}22` : 'rgba(247,248,250,0.97)',
        backdropFilter: 'blur(8px)',
        borderBottom: isOver ? `2px solid ${color}60` : '2px solid transparent',
        transition: 'all 0.15s ease',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <Typography fontWeight={700} fontSize="13px" sx={{ color: isOver ? color : 'text.primary' }}>
            {stage.name}
          </Typography>
          <Box sx={{ px: 1, py: 0.25, borderRadius: '10px', bgcolor: isOver ? color : '#f1f5f9' }}>
            <Typography fontSize="11px" fontWeight={700} sx={{ color: isOver ? '#fff' : '#64748b' }}>
              {isLoading ? '…' : count}
            </Typography>
          </Box>
        </Box>
        {!isClosedStage && (
          <IconButton size="small" onClick={() => onAddDeal(stage.name)}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* Column body + cards — scrollable inside column */}
      <Box
        onScroll={(e) => {
          const el = e.currentTarget
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage()
            }
          }
        }}
        sx={{
          flex: 1, borderTop: `3px solid ${color}`, pt: 1.5, minHeight: 150,
          maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', pr: 0.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: `${color}40`, borderRadius: '4px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          ...(isOver && stageDeals.length === 0 ? { display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={20} />
          </Box>
        ) : stageDeals.length === 0 ? (
          <Box sx={{
            p: 3, textAlign: 'center', border: isOver ? `2px solid ${color}` : '1.5px dashed #e2e8f0',
            borderRadius: '10px', bgcolor: isOver ? `${color}12` : '#f8fafc', mx: 0.5,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%', bgcolor: isOver ? color : `${color}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1,
            }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: isOver ? '#fff' : color, opacity: isOver ? 1 : 0.5 }} />
            </Box>
            <Typography fontSize="12px" fontWeight={700} sx={{ color }}>
              {isOver ? `Move to ${stage.name}` : 'No deals'}
            </Typography>
            <Typography fontSize="11px" color="text.secondary" sx={{ mt: 0.25 }}>
              {isOver ? 'Release to drop here' : 'Nothing here yet'}
            </Typography>
          </Box>
        ) : (
          <>
            {stageDeals.map(deal => (
              <DealsKanbanCard key={deal.id} deal={deal} canDrag={canDrag} onOpen={() => onOpen(deal)}
                onDragStart={() => setIsDraggingBoard(true)}
                onDragEnd={() => setIsDraggingBoard(false)} />
            ))}
            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={16} sx={{ color }} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

function DealsKanban({
  stageCounts = {},
  stages,
  baseFilters,
  canDrag = true,
  onOpen,
  onAddDeal,
  onDropDeal,
}: {
  stageCounts?: Record<string, { count: number; totalAmount: number }>
  stages: PipelineStage[]
  baseFilters: DealFilters
  canDrag?: boolean
  onOpen: (deal: Deal) => void
  onAddDeal: (stage?: string) => void
  onDropDeal: (dealId: string, targetStageName: string) => void
}) {
  const [, setIsDraggingBoard] = useState(false)

  const kanbanStages = useMemo(() => {
    const active = stages.filter(s => !s.isWon && !s.isLost)
    const won = stages.find(s => s.isWon)
    const lost = stages.find(s => s.isLost)

    const list: PipelineStage[] = [...active]

    const salesStage: PipelineStage = {
      id: 'virtual-sales',
      name: 'Sales',
      position: active.length + 1,
      color: '#7c3aed',
      probabilityPct: null,
      isWon: false,
      isLost: false,
      pipelineId: null,
      autoTaskTitle: null,
      autoTaskDueDays: null
    }
    list.push(salesStage)

    if (won) list.push(won)
    if (lost) list.push(lost)

    return list
  }, [stages])

  return (
    <Box sx={{ display: 'flex', gap: 2, pb: 2, alignItems: 'stretch', width: '100%' }}>
      {kanbanStages.map(stage => (
        <DealKanbanColumn
          key={stage.id}
          stage={stage}
          stageCount={stageCounts[stage.name]}
          baseFilters={baseFilters}
          canDrag={canDrag}
          onOpen={onOpen}
          onAddDeal={onAddDeal}
          onDropDeal={onDropDeal}
          setIsDraggingBoard={setIsDraggingBoard}
        />
      ))}
    </Box>
  )
}


function KanbanOrgPlaceholder({ orgs, onSelectOrg }: { orgs: any[]; onSelectOrg: (id: string) => void }) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 6,
        py: 8,
        borderRadius: '16px',
        border: `1px dashed ${themeColors.border}`,
        bgcolor: '#FAF9FB',
        maxWidth: 500,
        mx: 'auto',
        mt: 8,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '16px',
          bgcolor: '#EDE7FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          color: '#5C35D8',
        }}
      >
        <BusinessIcon sx={{ fontSize: 32 }} />
      </Box>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#2F2F3A' }}>
        Select an Organization
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 360, lineHeight: 1.5 }}>
        To view and manage the Kanban pipeline, please select a specific organization. Kanban boards are organization-specific.
      </Typography>

      <Stack spacing={2} sx={{ width: '100%', maxWidth: 300 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="kanban-org-select-label">Choose Organization</InputLabel>
          <Select
            labelId="kanban-org-select-label"
            label="Choose Organization"
            value=""
            onChange={(e) => onSelectOrg(e.target.value as string)}
            sx={{
              borderRadius: '8px',
              bgcolor: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: themeColors.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: themeColors.primary,
              },
            }}
          >
            {orgs.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {orgs.length > 0 && orgs.length <= 5 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1, textAlign: 'left' }}>
              Quick Select:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {orgs.map((org) => (
                <Button
                  key={org.id}
                  variant="outlined"
                  size="small"
                  onClick={() => onSelectOrg(org.id)}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderColor: '#E2E8F0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: themeColors.primary,
                      bgcolor: '#F5F3FF',
                      color: themeColors.primary,
                    },
                  }}
                >
                  {org.name}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  )
}

const DealsPage = observer(function DealsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-deals-filters')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return { ...EMPTY_UNIFIED_FILTERS, ...parsed, price: parsed.price ?? EMPTY_PRICE_FILTER }
      } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })
  const [page, setPage] = useState(1)
  const [pageSizeTable, setPageSizeTable] = useState(() => {
    const saved = localStorage.getItem('crm_deals_page_size')
    return saved ? Number(saved) : 10
  })

  useEffect(() => {
    sessionStorage.setItem('crm-deals-filters', JSON.stringify(filters))
  }, [filters])
  const [dealDrawerOpen, setDealDrawerOpen] = useState(false)
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null)
  const [deletingDeal, setDeletingDeal] = useState<Deal | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [bulkAssignUser, setBulkAssignUser] = useState<AdminUser | null>(null)
  const [markingDeal, setMarkingDeal] = useState<{ deal: Deal; status: DealStatus } | null>(null)
  const [lostDeal, setLostDeal] = useState<Deal | null>(null)
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>(() => {
    const saved = localStorage.getItem('crm_deals_view_mode')
    return (saved === 'kanban' || saved === 'table') ? saved : 'table'
  })
  const [kanbanOrgId, setKanbanOrgId] = useState<string | null>(() => {
    return sessionStorage.getItem('crm_deals_kanban_org_id')
  })

  useEffect(() => {
    if (kanbanOrgId) {
      sessionStorage.setItem('crm_deals_kanban_org_id', kanbanOrgId)
    } else {
      sessionStorage.removeItem('crm_deals_kanban_org_id')
    }
  }, [kanbanOrgId])

  useEffect(() => {
    localStorage.setItem('crm_deals_view_mode', viewMode)
  }, [viewMode])

  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['deals-list'] })
    qc.invalidateQueries({ queryKey: ['deals-kanban-col'] })
    qc.invalidateQueries({ queryKey: ['deals-summary'] })
    qc.invalidateQueries({ queryKey: ['deals-stats'] })
  }

  const { hasAction } = usePermission()
  const { isSuperAdmin } = useOrgSelector()
  const canCreate        = hasAction('/deals', 'create')
  const canEdit          = hasAction('/deals', 'update')
  const canDelete        = hasAction('/deals', 'delete')
  const canAssign        = hasAction('/deals', 'assign')
  const canManageStage   = hasAction('/deals', 'manage')
  const canUpdatePipeline = hasAction('/deals', 'update_pipeline')

  const { data: usersData } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }),
    staleTime: 60_000,
  })
  const usersList = usersData?.items ?? []

  // Org Admin tier (mirrors backend ITenantContext convention): has an orgId but no
  // specific lobId — sees/filters across every LOB in their own org, unlike a Normal User.
  const userOrgId  = authStore.user?.orgId ?? ''
  const userLobId  = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  // Global org filter from navbar (SuperAdmin only); local page dropdowns take precedence when set
  const navOrgFilter = useOrgFilter()

  const { data: orgs = [] } = useQuery({ queryKey: ['admin-orgs'], queryFn: orgsApi.getAll, enabled: isSuperAdmin, staleTime: 60_000 })
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  const lobIdForFetching = filters.checkboxes.lobId?.[0]
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dealsApi.delete(id),
    onSuccess: () => { invalidate(); toast.success('Deal deleted'); setDeletingDeal(null) },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected)
      await Promise.all(ids.map(id => dealsApi.delete(id)))
      return ids.length
    },
    onSuccess: (count) => {
      invalidate()
      setSelected(new Set())
      setBulkDeleteOpen(false)
      toast.success(`${count} deal(s) deleted`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete deals'),
  })

  const bulkAssignMutation = useMutation({
    mutationFn: async () => {
      if (!bulkAssignUser) return { count: 0, name: '' }
      const assignableDeals = selectedDeals.filter(d => d.status === 'OPEN')
      if (assignableDeals.length === 0) return { count: 0, name: '' }
      const name = bulkAssignUser.name
      await Promise.all(assignableDeals.map(d => dealsApi.update(d.id, {
        title: d.title,
        stage: d.stage,
        assignedToId: bulkAssignUser.id,
      })))
      return { count: assignableDeals.length, name }
    },
    onSuccess: ({ count, name }) => {
      invalidate()
      setSelected(new Set())
      setBulkAssignOpen(false)
      setBulkAssignUser(null)
      toast.success(`${count} deal(s) reassigned to ${name}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to reassign deals'),
  })

  const changeStageMutation = useMutation({
    mutationFn: ({ id, deal, stage }: { id: string; deal: Deal; stage: string }) =>
      dealsApi.changeStage(id, deal, stage, stages),
    onSuccess: (updated) => {
      toast.success(`Moved to "${updated.stage}"`)
      qc.invalidateQueries({ queryKey: ['deals-list'], refetchType: 'none' })
      qc.invalidateQueries({ queryKey: ['deals-stats'], refetchType: 'none' })
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to update stage")
      qc.invalidateQueries({ queryKey: ['deals-kanban-col'] })
      qc.invalidateQueries({ queryKey: ['deals-summary'] })
    },
  })

  const handleDropDeal = (dealId: string, targetStageName: string) => {
    let deal: Deal | undefined
    if (viewMode === 'kanban') {
      const queryCache = qc.getQueryCache().findAll({ queryKey: ['deals-kanban-col'] })
      for (const q of queryCache) {
        const d = q.state.data as any
        if (d?.pages) {
          for (const page of d.pages) {
            const found = page.items?.find((item: any) => item.id === dealId)
            if (found) { deal = found; break }
          }
        }
        if (deal) break
      }
    } else {
      deal = deals.find(d => d.id === dealId)
    }
    if (!deal) return

    // Won/Lost/Sales status deals are immutable
    if (deal.status !== 'OPEN') {
      toast.error("Won, Lost or Sales deals cannot be moved")
      return
    }

    const wonStage = stages.find(s => s.isWon)
    const lostStage = stages.find(s => s.isLost)
    const activeStages = stages.filter(s => !s.isWon && !s.isLost)

    const currentStageName = deal.stage
    const currentIdx = activeStages.findIndex(s => s.name === currentStageName)

    if (targetStageName === currentStageName) {
      return
    }

    if (lostStage && targetStageName === lostStage.name) {
      setLostDeal(deal)
      return
    }

    if (wonStage && targetStageName === wonStage.name) {
      toast.error("Deals can only be marked as Won from the Sales page")
      return
    }

    if (targetStageName === 'Sales') {
      // Must be at the last active stage to move to Sales
      const isAtLastActiveStage = currentIdx !== -1 && currentIdx === activeStages.length - 1
      if (!isAtLastActiveStage) {
        toast.error("Deals can only be moved to Sales once all pipeline stages are completed")
        return
      }
      setMarkingDeal({ deal, status: 'SALES' })
      return
    }

    // Active stage checks: sequential forward only
    const targetIdx = activeStages.findIndex(s => s.name === targetStageName)
    if (targetIdx !== -1) {
      if (targetIdx === currentIdx + 1) {
        const oldStage = deal.stage
        const updatedDeal = { ...deal, stage: targetStageName, updatedAt: new Date().toISOString() }

        // Remove from old stage column cache
        qc.setQueriesData(
          { queryKey: ['deals-kanban-col', oldStage] },
          (old: any) => {
            if (!old?.pages) return old
            return {
              ...old,
              pages: old.pages.map((p: any) => ({
                ...p,
                items: p.items.filter((item: any) => item.id !== dealId),
                total: Math.max(0, (p.total || 0) - 1),
              })),
            }
          }
        )

        // Add to new stage column cache
        qc.setQueriesData(
          { queryKey: ['deals-kanban-col', targetStageName] },
          (old: any) => {
            if (!old?.pages || old.pages.length === 0) return old
            const firstPage = old.pages[0]
            const exists = firstPage.items?.some((item: any) => item.id === dealId)
            if (exists) return old
            return {
              ...old,
              pages: [
                {
                  ...firstPage,
                  items: [updatedDeal, ...firstPage.items],
                  total: (firstPage.total || 0) + 1,
                },
                ...old.pages.slice(1),
              ],
            }
          }
        )

        // Optimistically update summary counts
        qc.setQueriesData(
          { queryKey: ['deals-summary'] },
          (old: any) => {
            if (!old?.stageSummaries) return old
            return {
              ...old,
              stageSummaries: old.stageSummaries.map((s: any) => {
                if (s.stage === oldStage) return { ...s, count: Math.max(0, s.count - 1) }
                if (s.stage === targetStageName) return { ...s, count: s.count + 1 }
                return s
              }),
            }
          }
        )

        changeStageMutation.mutate({ id: deal.id, deal, stage: targetStageName })
      } else {
        toast.error("Deals can only move to the immediate next stage")
      }
    }
  }

  // Local page filter takes precedence; navbar org filter is the fallback for SuperAdmin
  // For Kanban view, when navOrgFilter is not set, we use the local kanbanOrgId selection.
  // For Table view, we use the dialog filters (filters.checkboxes.orgId).
  const effectiveOrgId = isSuperAdmin
    ? navOrgFilter.orgId || kanbanOrgId
    : userOrgId

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [showScrollTop, setShowScrollTop] = useState(false)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setShowScrollTop(e.currentTarget.scrollTop > 200)
  }

  const effectiveOrgIds = isSuperAdmin
    ? navOrgFilter.orgId
      ? [navOrgFilter.orgId]
      : viewMode === 'kanban'
        ? kanbanOrgId ? [kanbanOrgId] : undefined
        : filters.checkboxes.orgId?.length
          ? filters.checkboxes.orgId
          : undefined
    : undefined

  const { data: stages = [] } = useQuery({
    queryKey: ['pipeline-stages', effectiveOrgId],
    queryFn: () => dealsApi.getStages(undefined, effectiveOrgId),
  })

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  const KANBAN_PAGE_SIZE = 50

  // ── Base filters (shared between table and kanban) ───────────────────────────
  const baseFilters = {
    search: search || undefined,
    statuses: filters.checkboxes.status?.length ? filters.checkboxes.status as DealStatus[] : undefined,
    stages: filters.checkboxes.stage?.length ? filters.checkboxes.stage : undefined,
    sources: filters.checkboxes.source?.length ? filters.checkboxes.source : undefined,
    ownerIds: filters.ownerIds?.length ? filters.ownerIds : undefined,
    assignedToIds: filters.checkboxes.userId?.length ? filters.checkboxes.userId : undefined,
    fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
    toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
    orgIds: effectiveOrgIds,
    lobIds: (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined,
    warehouseIds: undefined,
    customerTypeIds: filters.checkboxes.customerTypeId?.length ? filters.checkboxes.customerTypeId.map(Number) : undefined,
    amountLt:  filters.price?.operator === 'lt'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGt:  filters.price?.operator === 'gt'    && filters.price.value1 ? parseFloat(filters.price.value1)
             : filters.price?.operator === 'lt_gt' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountEq:  filters.price?.operator === 'eq'    && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountLte: filters.price?.operator === 'lt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    amountGte: filters.price?.operator === 'gt_eq' && filters.price.value1 ? parseFloat(filters.price.value1) : undefined,
    ...(filters.price?.operator === 'lt_gt' && filters.price.value2 ? { amountLt: parseFloat(filters.price.value2) } : {}),
  }

  // ── Table query (regular pagination) ───────────────────────────────────────
  const apiFilters = { ...baseFilters, page, pageSize: pageSizeTable }

  const { data, isLoading } = useQuery({
    queryKey: ['deals-list', apiFilters],
    queryFn: () => dealsApi.getAll(apiFilters),
    placeholderData: prev => prev,
    staleTime: 0,
    enabled: viewMode === 'table',
  })

  const deals = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const { data: dealsSummary } = useQuery({
    queryKey: ['deals-summary', baseFilters],
    queryFn: () => dealsApi.getSummary(baseFilters),
    staleTime: 0,
  })

  const stageCountsMap = useMemo(() => {
    const map: Record<string, { count: number; totalAmount: number }> = {}
    if (dealsSummary?.stageSummaries) {
      for (const s of dealsSummary.stageSummaries) {
        map[s.stage] = { count: s.count, totalAmount: s.totalAmount }
      }
    }
    return map
  }, [dealsSummary])

  // Complete list of sources from the DB (localStorage-cached) — NOT derived from
  // the current paginated response, so the Source filter is identical on every page.
  const { options: sourceFilterOptions } = useDealSourceFilterOptions()

  const statsFilters = useMemo(() => ({
    search: apiFilters.search,
    stages: apiFilters.stages,
    ownerIds: apiFilters.ownerIds,
    orgIds: apiFilters.orgIds,
    lobIds: apiFilters.lobIds,
    page: 1,
    pageSize: 1000,
  }), [
    apiFilters.search,
    apiFilters.stages,
    apiFilters.ownerIds,
    apiFilters.orgIds,
    apiFilters.lobIds,
  ])

  const wonDealsCount = dealsSummary?.wonDealsCount ?? 0
  const lostDealsCount = dealsSummary?.lostDealsCount ?? 0
  const totalClosedDeals = wonDealsCount + lostDealsCount
  const conversionRate = totalClosedDeals > 0 ? (wonDealsCount / totalClosedDeals) * 100 : 0

  const isWonFilterActive = filters.checkboxes.status?.includes('WON') && filters.checkboxes.status.length === 1
  const isLostFilterActive = filters.checkboxes.status?.includes('LOST') && filters.checkboxes.status.length === 1

  const handleToggleWonFilter = () => {
    setFilters(prev => {
      const isCurrentlyWon = prev.checkboxes.status?.includes('WON') && prev.checkboxes.status.length === 1
      return {
        ...prev,
        checkboxes: {
          ...prev.checkboxes,
          status: isCurrentlyWon ? [] : ['WON']
        }
      }
    })
    setPage(1)
  }

  const handleToggleLostFilter = () => {
    setFilters(prev => {
      const isCurrentlyLost = prev.checkboxes.status?.includes('LOST') && prev.checkboxes.status.length === 1
      return {
        ...prev,
        checkboxes: {
          ...prev.checkboxes,
          status: isCurrentlyLost ? [] : ['LOST']
        }
      }
    })
    setPage(1)
  }

  const headerStats = [
    {
      label: 'Won Deals',
      value: wonDealsCount,
      onClick: handleToggleWonFilter,
      active: isWonFilterActive,
      color: themeColors.success,
      icon: <WonIcon sx={{ fontSize: 18 }} />
    },
    {
      label: 'Lost Deals',
      value: lostDealsCount,
      onClick: handleToggleLostFilter,
      active: isLostFilterActive,
      color: themeColors.error,
      icon: <LostIcon sx={{ fontSize: 18 }} />
    },
    {
      label: 'Conversion Rate',
      value: `${conversionRate.toFixed(1)}%`,
      // value: '-',
      icon: <DealIcon sx={{ fontSize: 18 }} />,
      color: themeColors.primary
    },
    {
      label: 'Avg Deal Value',
      // value: formatAmount(averageDealValue, 'INR'),
      value: '-',
      icon: <RevenueIcon sx={{ fontSize: 18 }} />,
      color: '#059669'
    }
  ]

  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = [
      {
        type: 'checkbox',
        key: 'status',
        label: 'Status',
        options: [
          { label: 'Open', value: 'OPEN' },
          { label: 'Sales', value: 'SALES' },
          { label: 'Won', value: 'WON' },
          { label: 'Lost', value: 'LOST' },
        ],
      },
      {
        type: 'checkbox',
        key: 'stage',
        label: 'Stage',
        options: Array.from(new Set([...stages.map(s => s.name), ...deals.map(d => d.stage)])).filter(Boolean)
          .map(s => ({ label: s, value: s })),
      },
      {
        type: 'checkbox',
        key: 'source',
        label: 'Source',
        options: sourceFilterOptions,
      },
      {
        type: 'checkbox',
        key: 'customerTypeId',
        label: 'Customer Type',
        options: customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) })),
      },
      { type: 'price', label: 'Price' },
      { type: 'date', label: 'Date Created' },
    ]

    if (authStore.canViewLobWideData) {
      defs.push(
        {
          type: 'checkbox',
          key: 'userId',
          label: 'Assignee',
          options: [
            { label: 'Unassigned', value: 'unassigned' },
            ...usersList.map(u => ({ label: u.name, value: u.id })),
          ],
        },
        { type: 'owner', label: 'Owner' },
      )
    }

    if (isSuperAdmin) {
      defs.push(
        {
          type: 'checkbox',
          key: 'orgId',
          label: 'Organisation',
          options: orgs.map(o => ({ label: o.name, value: o.id })),
        },
        {
          type: 'checkbox',
          key: 'lobId',
          label: 'LOB',
          options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
        },
      )
    } else if (isOrgAdmin) {
      // Org Admin's org is fixed (no Organisation picker needed) — just let them
      // narrow the list down to one or more LOBs within their own org.
      defs.push({
        type: 'checkbox',
        key: 'lobId',
        label: 'LOB',
        options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
      })
    }

    return defs
  }, [stages, deals, sourceFilterOptions, customerTypes, isSuperAdmin, isOrgAdmin, orgs, lobsForFilter, usersList])

  const dealColumns: Column[] = [
    { id: 'title', label: 'Deal', sortable: true, render: (_, deal: Deal) => deal.title },
    { id: 'company', label: 'Company', sortable: true, render: (_, deal: Deal) => deal.customer?.companyName || deal.customer?.name || '-' },
    { id: 'contact', label: 'Contact Name', sortable: true, render: (_, deal: Deal) => deal.contact?.name ?? deal.lead?.name ?? '-' },
    {
      id: 'amount',
      label: 'Expected Value',
      sortable: true,
      render: (_, deal: Deal) => (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2F2F3A' }}>
          {formatAmount(deal.amount, deal.currency)}
        </Typography>
      ),
    },
    { id: 'expectedCloseDate', label: 'Expected Closing Date', sortable: true, render: (_, deal: Deal) => formatDate(deal.expectedCloseDate) },
    {
      id: 'owner',
      label: 'Owner',
      sortable: true,
      render: (_, deal: Deal) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Avatar sx={{ width: 21, height: 21, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(deal.owner.name) }}>
            {initials(deal.owner.name)}
          </Avatar>
          <Typography sx={{ fontSize: '12px', color: '#2F2F3A', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {deal.owner.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'stage',
      label: 'Stage',
      sortable: true,
      render: (_, deal: Deal) => {
        const isSalesStage = deal.status === 'SALES' || deal.stage === 'Sales'
        const labelText = isSalesStage ? 'Sales' : deal.stage
        const stg = isSalesStage ? null : stages.find(s => s.name === deal.stage)
        const color = isSalesStage ? '#7c3aed' : (stg?.color || '#6366f1')
        return (
          <Chip
            label={labelText}
            size="small"
            sx={{
              height: 20,
              borderRadius: '4px',
              bgcolor: `${color}18`,
              color: color,
              border: `1px solid ${color}44`,
              fontSize: '11px',
              fontWeight: 600,
              '& .MuiChip-label': { px: '8px' },
            }}
          />
        )
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: 112,
      sortable: false,
      render: (_, deal: Deal) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          {canManageStage && deal.status === 'OPEN' && (
            <Tooltip title="Mark as Lost">
              <IconButton size="small" onClick={() => setMarkingDeal({ deal, status: 'LOST' })}
                sx={{ color: '#7A7580', '&:hover': { color: themeColors.error, bgcolor: themeColors.errorLight } }}>
                <LostIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={(deal.status === 'WON' || deal.status === 'SALES') ? "Deal is moved to Sales/Won" : "Edit"}>
              <Box component="span" sx={{ display: 'inline-flex', cursor: (deal.status === 'WON' || deal.status === 'SALES') ? 'not-allowed' : 'inherit' }}>
                <IconButton size="small" onClick={() => setEditingDeal(deal)}
                  disabled={deal.status === 'WON' || deal.status === 'SALES'}
                  sx={{ color: '#7A7580', '&:hover': { color: '#2F1E57', bgcolor: '#F2EEF9' } }}>
                  <EditActionIcon />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => setDeletingDeal(deal)}
                sx={{ color: '#D92D20', '&:hover': { bgcolor: '#FFE5E5' } }}>
                <DeleteActionIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  const selectedDeals = useMemo(() => deals.filter(d => selected.has(d.id)), [deals, selected])
  const openDealsInSelection = useMemo(() => selectedDeals.filter(d => d.status === 'OPEN'), [selectedDeals])
  const closedDealsInSelection = useMemo(() => selectedDeals.filter(d => d.status !== 'OPEN'), [selectedDeals])

  const selectedOrgIds = useMemo(() => Array.from(new Set(selectedDeals.map(d => d.orgId).filter(Boolean))), [selectedDeals])
  const hasMultipleOrgsSelected = selectedOrgIds.length > 1
  const targetOrgId = selectedOrgIds.length === 1 ? selectedOrgIds[0] : null

  const selectedLobIds = useMemo(() => Array.from(new Set(selectedDeals.map(d => d.lobId).filter(Boolean))), [selectedDeals])
  const hasMultipleLobsSelected = selectedLobIds.length > 1
  const targetLobId = selectedLobIds.length === 1 ? selectedLobIds[0] : null

  const filteredUsersList = useMemo(() => {
    return usersList.filter(u => {
      if (u.roleName?.toUpperCase() === 'SUPER_ADMIN') return false
      if (targetOrgId && u.orgId !== targetOrgId) return false
      if (!u.lobId) return true // Org Admin — eligible across all LOBs in the org
      if (hasMultipleLobsSelected) return false // normal user can't cover multiple LOBs at once
      if (targetLobId && u.lobId !== targetLobId) return false
      return true
    })
  }, [usersList, targetOrgId, targetLobId, hasMultipleLobsSelected])

  const bulkActions: BulkAction[] = useMemo(() => {
    const actions: BulkAction[] = []
    if (canAssign) {
      actions.push({
        label: 'Assign to...',
        onClick: () => setBulkAssignOpen(true),
      })
    }
    if (canDelete) {
      actions.push({
        label: 'Delete',
        color: 'error',
        onClick: () => setBulkDeleteOpen(true),
      })
    }
    return actions
  }, [canAssign, canDelete])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Deals"
        breadcrumb="CRM"
        stats={headerStats}
        actions={canCreate ? (
          <PageActionButtons
            onNew={() => setDealDrawerOpen(true)}
            onExport={async () => {
              await dealsApi.export({ ...apiFilters, search })
            }}
            newLabel="New Deal"
          />
        ) : (
          <PageActionButtons
            onExport={async () => {
              await dealsApi.export({ ...apiFilters, search })
            }}
          />
        )}
      />

      {/* ── Pinned toolbar (search + filters) ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0, minWidth: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder="Search deals..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedCount={selected.size}
          bulkActions={bulkActions}
          onClearSelection={() => setSelected(new Set())}
          leftExtra={
            isSuperAdmin && !navOrgFilter.orgId && viewMode === 'kanban' && kanbanOrgId ? (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={kanbanOrgId}
                  onChange={(e) => setKanbanOrgId(e.target.value as string || null)}
                  displayEmpty
                  sx={{
                    height: '38px',
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: themeColors.primary,
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '13px', fontStyle: 'italic' }}>
                    All Organizations
                  </MenuItem>
                  {orgs.map((org) => (
                    <MenuItem key={org.id} value={org.id} sx={{ fontSize: '13px' }}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : undefined
          }
        />
      </Box>

      {/* ── Scrollable content ── */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, overflowY: 'auto', overflowX: 'auto', position: 'relative' }}
      >

        {/* ── View Content ── */}
        {viewMode === 'table' ? (
          <DataTable
            columns={dealColumns}
            rows={deals}
            isLoading={isLoading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            showRowNumbers={false}
            onRowClick={(deal) => navigate(`/deals/${deal.id}`)}
            emptyIcon={<DealIcon />}
            emptyMessage={search || countActiveFilters(filters) > 0 ? 'No deals match your filters' : 'No deals found'}
            emptyHint={search || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Create your first deal to get started'}
            pagination={{
              page,
              pageSize: pageSizeTable,
              total,
              totalPages,
              itemLabel: 'results',
              onPageChange: setPage,
              onPageSizeChange: s => { setPageSizeTable(s); localStorage.setItem('crm_deals_page_size', String(s)); setPage(1) },
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        ) : isSuperAdmin && !effectiveOrgId ? (
          <KanbanOrgPlaceholder orgs={orgs} onSelectOrg={setKanbanOrgId} />
        ) : (
          <>
            <DealsKanban
              stageCounts={stageCountsMap}
              stages={stages}
              baseFilters={baseFilters}
              canDrag={canUpdatePipeline}
              onOpen={(deal) => navigate(`/deals/${deal.id}`)}
              onAddDeal={() => setDealDrawerOpen(true)}
              onDropDeal={handleDropDeal}
            />

            {/* Scroll to top button */}
            {showScrollTop && (
              <Tooltip title="Scroll to top" placement="left">
                <IconButton
                  onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    position: 'fixed',
                    bottom: 28,
                    right: 28,
                    zIndex: 1200,
                    bgcolor: themeColors.primary,
                    color: '#ffffff',
                    width: 44,
                    height: 44,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
                    '&:hover': {
                      bgcolor: themeColors.primary,
                      filter: 'brightness(1.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                    },
                    transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  <ScrollTopIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

      </Box>

      {/* ── Dialogs ── */}
      <DealDrawer
        open={dealDrawerOpen}
        onClose={() => setDealDrawerOpen(false)}
        isSubmitting={false}
        onSubmit={(data: DealDrawerValues) => {
          const payload: CreateDealPayload = {
            title: data.dealName,
            customerId: data.customerId || undefined,
            contactId: data.contactId || undefined,
            stage: data.stage || undefined,
            amount: data.amount ? parseFloat(data.amount) : undefined,
            currency: data.currency || 'INR',
            probabilityPct: data.probabilityPct ? parseInt(data.probabilityPct) : undefined,
            priority: data.priority || undefined,
            source: data.source || undefined,
            customerTypeId: data.customerTypeId ? parseInt(data.customerTypeId) : undefined,
            description: data.description || undefined,
            expectedCloseDate: data.expectedClosingDate || undefined,
            orgId: data.orgId || effectiveOrgId,
            lobId: data.lobId || undefined,
            requirements: data.requirements || undefined,
          }
          dealsApi.create(payload).then((res) => {
            invalidate()
            toast.success('Deal created')
            setDealDrawerOpen(false)
            if (res?.id) {
              navigate(`/deals/${res.id}`)
            }
          }).catch((e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create deal'))
        }}
      />

      {editingDeal && (
        <EditDealModal
          open={!!editingDeal}
          deal={editingDeal}
          stages={stages}
          onClose={() => setEditingDeal(null)}
        />
      )}

      <DeleteDialog
        open={bulkDeleteOpen}
        title={`Delete ${selected.size} Deal(s)`}
        description={`Are you sure you want to delete ${selected.size} selected deal(s)? This action cannot be undone.`}
        isDeleting={bulkDeleteMutation.isPending}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate()}
      />

      <Dialog
        open={bulkAssignOpen}
        onClose={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '16px' }}>
          Assign {openDealsInSelection.length} Selected Deal(s)
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {closedDealsInSelection.length > 0 && (
            <Typography fontSize="12.5px" color="warning.main" sx={{ pb: 1, fontWeight: 600 }}>
              ⚠️ {closedDealsInSelection.length} selected deal(s) are closed (Won/Lost/Sales) and cannot be reassigned. Only OPEN deals will be reassigned.
            </Typography>
          )}
          {hasMultipleOrgsSelected ? (
            <Typography fontSize="13px" color="error" sx={{ py: 1, fontWeight: 500 }}>
              ⚠️ The selected deals belong to different organizations. Bulk assignment is only allowed for deals belonging to the same organization.
            </Typography>
          ) : (
            <>
              {hasMultipleLobsSelected && (
                <Typography fontSize="12px" color="text.secondary" sx={{ pb: 1 }}>
                  Selected deals span multiple LOBs — only Organization Admins are eligible for this bulk assignment.
                </Typography>
              )}
              <FormSelect
                label="Assign To User"
                value={bulkAssignUser?.id ?? ''}
                onChange={(val: any) => {
                  const user = filteredUsersList.find(u => u.id === val?.value)
                  setBulkAssignUser(user ?? null)
                }}
                options={filteredUsersList.map(u => ({ label: `${u.name} (${u.email})`, value: u.id }))}
                fullWidth
              />
              {filteredUsersList.length === 0 && (
                <Typography fontSize="12px" color="error" sx={{ pt: 1 }}>
                  No eligible users found. Only users in the same LOB, or Organization Admins, can be assigned.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={hasMultipleOrgsSelected || openDealsInSelection.length === 0 || !bulkAssignUser || bulkAssignMutation.isPending}
            onClick={() => bulkAssignMutation.mutate()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: themeColors.primary,
              '&:hover': { bgcolor: themeColors.primaryDark },
            }}
          >
            {bulkAssignMutation.isPending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirm Assignment'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteDialog
        open={!!deletingDeal}
        title="Delete Deal"
        name={deletingDeal?.title}
        onClose={() => setDeletingDeal(null)}
        onConfirm={() => deletingDeal && deleteMutation.mutate(deletingDeal.id)}
        isDeleting={deleteMutation.isPending}
      />

      {markingDeal && (
        <MarkStatusDialog
          open={!!markingDeal}
          deal={markingDeal.deal}
          targetStatus={markingDeal.status}
          onClose={() => setMarkingDeal(null)}
        />
      )}

      {lostDeal && (
        <MarkLostModal
          open={!!lostDeal}
          deal={lostDeal}
          onClose={() => setLostDeal(null)}
          onSuccess={() => invalidate()}
        />
      )}
    </Box>
  )
})

export default DealsPage
